import { CheckOutlined, DeleteOutlined } from '@ant-design/icons';
import { App, Button, Form, Spin } from 'antd';
import type {
    TabelaPrecoProduto,
    TabelaPrecoProdutoExcecao,
} from '@/features/tabela-preco/types/TabelaPrecoProduto';
import type { Dialog } from '@/std/dialog/types';
import { useMutationTabelaPrecoAlterar } from '@/features/tabela-preco/api/apiTabelaPrecoAlterar';
import { SelectTabelaPrecoExcecaoTipo } from '@/features/tabela-preco/components/SelectTabelaPrecoExcecaoTipo';
import { TabelaPrecoExcecaoFormFieldValor } from '@/features/tabela-preco/components/TabelaPrecoExcecaoFormFieldValor';
import { TabelaPrecoExcecaoTipoPreco } from '@/features/tabela-preco/types/enum';
import { getTabelaPrecoValue } from '@/features/tabela-preco/util/getTabelaPrecoValue';
import { numberToCurrency } from '@/std/format';
import { attempt } from '@/std/result';

type FormValues = Pick<TabelaPrecoProdutoExcecao, 'tpe_preco' | 'tpe_tipo_preco' | 'tpe_idpk'>;

export function ProdutoTabTabelaPrecoDrawerExcecaoForm({
    dialog,
    tabelaPreco,
    produtoIdpk,
}: {
    dialog: Dialog<TabelaPrecoProduto>;
    tabelaPreco: TabelaPrecoProduto;
    produtoIdpk: number;
}) {
    const { notification } = App.useApp();
    const [form] = Form.useForm<FormValues>();

    const tpe_tipo_preco = Form.useWatch('tpe_tipo_preco', form);
    const tpe_preco = Form.useWatch('tpe_preco', form);
    const tpe_idpk = Form.useWatch('tpe_idpk', form);

    const mutationPut = useMutationTabelaPrecoAlterar();

    const onRemove = async (): Promise<void> => {
        if (!tpe_idpk) {
            notification.error({
                description: 'ID da exceção não encontrado.',
                title: 'Erro ao remover exceção da tabela de preço',
            });

            return;
        }

        const result = await attempt(
            mutationPut.mutateAsync({
                body: {
                    ...tabelaPreco,
                    tabela_preco_excecao: [
                        {
                            excluir: 'S',
                            tpe_idpk,
                        },
                    ],
                },
                idpk: tabelaPreco.tap_idpk,
            }),
        );

        if (result.error) {
            notification.error({
                description: result.error.message,
                title: 'Erro ao remover exceção da tabela de preço',
            });

            return;
        }

        notification.success({
            title: 'Exceção da tabela de preço removida com sucesso',
        });

        dialog.actions.close();
    };

    const onFinish = async (values: FormValues): Promise<void> => {
        const result = await attempt(
            mutationPut.mutateAsync({
                body: {
                    ...tabelaPreco,
                    tabela_preco_excecao: [
                        {
                            ...values,
                            tpe_idpk: tabelaPreco.tabela_preco_excecao?.tpe_idpk,
                            tpe_produto_idpk: produtoIdpk,
                            tpe_produto_pacote_barra_idpk:
                                tabelaPreco.produto_pacote_barra?.pcb_idpk ?? null,
                        },
                    ],
                },
                idpk: tabelaPreco.tap_idpk,
            }),
        );

        if (result.error) {
            notification.error({
                description: result.error.message,
                title: 'Erro ao salvar exceção da tabela de preço',
            });

            return;
        }

        notification.success({
            title: 'Exceção da tabela de preço salva com sucesso',
        });

        dialog.actions.close();
    };

    const precoCalculado = getTabelaPrecoValue({
        produto: tabelaPreco.produto,
        produto_pacote_barra: tabelaPreco.produto_pacote_barra,
        tpe_preco,
        tpe_tipo_preco,
    });

    return (
        <Spin spinning={mutationPut.isPending}>
            <Form
                form={form}
                initialValues={{
                    tpe_idpk: tabelaPreco.tabela_preco_excecao?.tpe_idpk ?? null,
                    tpe_preco: tabelaPreco.tabela_preco_excecao?.tpe_preco ?? null,
                    tpe_tipo_preco:
                        tabelaPreco.tabela_preco_excecao?.tpe_tipo_preco ??
                        TabelaPrecoExcecaoTipoPreco.PrecoEspecificado,
                }}
                layout='vertical'
                onFinish={onFinish}
            >
                <Form.Item hidden={true} name='tpe_idpk'>
                    <input />
                </Form.Item>

                <Form.Item
                    label='Tipo da exceção'
                    name='tpe_tipo_preco'
                    required={true}
                    rules={[
                        {
                            message: 'Favor informar o tipo de variação',
                            required: true,
                        },
                    ]}
                >
                    <SelectTabelaPrecoExcecaoTipo isProduto={true} />
                </Form.Item>

                <TabelaPrecoExcecaoFormFieldValor label='Valor' tpe_tipo_preco={tpe_tipo_preco} />

                <div className='mb-4 flex justify-center'>
                    <p>Preço calculado: {numberToCurrency(precoCalculado)}</p>
                </div>

                <div className='flex justify-center gap-2'>
                    {tpe_idpk && (
                        <Button danger={true} icon={<DeleteOutlined />} onClick={onRemove}>
                            Remover
                        </Button>
                    )}

                    <Button htmlType='submit' icon={<CheckOutlined />} type='primary'>
                        Salvar
                    </Button>
                </div>
            </Form>
        </Spin>
    );
}
